import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { Input, Empty, Select } from "antd";
import { toast } from "react-toastify";

import useHealthChecks from "hooks/health-checks/useHealthChecks";
import useDebounce from "hooks/route-rules/useDebounce";
import paths from "../../../routes/paths";


import  HealthChecksTable from "./HeathChecksTable"
import HealthCheckDrawer from "./HealthCheckDrawer";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import IconButton from "components/common/IconButton";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";

const HealthChecks = () => {
    const POLL_INTERVAL_MS = 15000;
    const navigate = useNavigate();
    const { checkId } = useParams();
    const location = useLocation();

    // State
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("ALL");
    const [selectedEnabled, setSelectedEnabled] = useState("ALL");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [checkToDelete, setCheckToDelete] = useState(null);
    const [drawerHealthCheck, setDrawerHealthCheck] = useState(null);
    const isMutatingRef = useRef(false);

    const {
        healthChecks= [],
        loading,
        pagination,
        fetchAllHealthChecks,
        searchHealthChecks,
        fetchHealthCheckById,
        updateHealthCheck,
        addHealthCheck,
        deleteHealthCheck,
        runProbe,
    } = useHealthChecks(page, pageSize);

    // URL Logic
    const isCreating = location.pathname.endsWith('/add');
    const isEditing = !!checkId && !isCreating;
    const isDrawerOpen = isCreating || isEditing;

    const handleOpenAddDrawer = () => navigate(paths.healthCheckAdd);
    const handleOpenEditDrawer = (input) => {
      const id = typeof input === "object" ? input?.id : input;
      if (!id) {
        toast.error("Health check ID is missing. Please refresh and try again.");
        return;
      }
      if (typeof input === "object") {
        setDrawerHealthCheck(input);
      }
      navigate(paths.healthCheckDetail(id));
    };
    const handleCloseDrawer = () => navigate(paths.healthChecks);
    const selectedHealthCheckFromList = healthChecks.find(
      (c) => String(c.id) === String(checkId)
    );
    const selectedHealthCheck =
      selectedHealthCheckFromList ||
      (drawerHealthCheck && String(drawerHealthCheck.id) === String(checkId)
        ? drawerHealthCheck
        : null);
    const statusOptions = useMemo(
      () => [
        { label: "\u{1F310} ALL", value: "ALL" },
        { label: "\u2139\uFE0F INFO", value: "INFO" },
        { label: "\u26A0\uFE0F ALERT", value: "ALERT" },
        { label: "\u{1F7E0} MEDIUM", value: "MEDIUM" },
        { label: "\u{1F525} CRITICAL", value: "CRITICAL" },
      ],
      []
    );
    const enabledOptions = useMemo(
      () => [
        { label: "\u{1F310} ALL", value: "ALL" },
        { label: "\u2705 ENABLED", value: "ENABLED" },
        { label: "\u26D4 DISABLED", value: "DISABLED" },
      ],
      []
    );

    // Search Logic
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const isSearchRequest = debouncedSearchTerm.trim().length >= 3;

    const refreshCurrentView = useCallback(() => {
        if (isMutatingRef.current) return;

        const trimmed = debouncedSearchTerm.trim();
        const hasStatusFilter = selectedStatus !== "ALL";
        const hasEnabledFilter = selectedEnabled !== "ALL";

        if (
          trimmed.length === 0 &&
          !hasStatusFilter &&
          !hasEnabledFilter
        ) {
            fetchAllHealthChecks(page);
            return;
        }

        if (
          trimmed.length > 0 &&
          trimmed.length < 3 &&
          !hasStatusFilter &&
          !hasEnabledFilter
        ) {
            fetchAllHealthChecks(page);
            return;
        }

        searchHealthChecks(
          trimmed,
          selectedStatus,
          "ALL",
          selectedEnabled,
          page
        );
    }, [
      debouncedSearchTerm,
      selectedStatus,
      selectedEnabled,
      page,
      searchHealthChecks,
      fetchAllHealthChecks,
    ]);

    useEffect(() => {
      refreshCurrentView();
    }, [refreshCurrentView]);

    useEffect(() => {
      const intervalId = setInterval(() => {
        refreshCurrentView();
      }, POLL_INTERVAL_MS);

      return () => clearInterval(intervalId);
    }, [
      refreshCurrentView,
      POLL_INTERVAL_MS,
    ]);

    useEffect(() => {
      if (!isDrawerOpen) {
        setDrawerHealthCheck(null);
        return;
      }

      if (selectedHealthCheckFromList) {
        setDrawerHealthCheck(selectedHealthCheckFromList);
        return;
      }

      if (isEditing && checkId) {
        fetchHealthCheckById(checkId)
          .then((data) => {
            if (data) setDrawerHealthCheck(data);
          })
          .catch(() => {});
      }
    }, [
      isDrawerOpen,
      isEditing,
      checkId,
      selectedHealthCheckFromList,
      fetchHealthCheckById,
    ]);

    // Handlers
    const handleToggleActive = async (check) => {
        try {
            isMutatingRef.current = true;
            const currentlyEnabled =
                typeof check.active === "boolean"
                    ? check.active
                    : !!check.isEnabled;
            const nextEnabled = !currentlyEnabled;

            await updateHealthCheck(check.id, {
                isEnabled: nextEnabled,
            });
            // Run a slightly delayed refresh to avoid racing with backend transaction visibility.
            setTimeout(() => {
              isMutatingRef.current = false;
              refreshCurrentView();
            }, 350);
        } catch (err) {
            isMutatingRef.current = false;
            toast.error("Failed to toggle status");
        }
    };

    const handleDeleteConfirm = async () => {
        setIsSubmitting(true);
        try {
            await deleteHealthCheck(checkToDelete);
            setShowDeleteModal(false);
            // If we delete the last visible row on this page, move back one page.
            if ((healthChecks?.length || 0) <= 1 && page > 0) {
              setPage((prev) => Math.max(prev - 1, 0));
            } else {
              refreshCurrentView();
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSaveHealthCheck = async (data) => {
      setIsSubmitting(true);
      try {
        await (isCreating
          ? await addHealthCheck(data)
          : await updateHealthCheck(checkId, data));
        navigate(paths.healthChecks);
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
    <>
      <WidgetSectionTitle
        icon="briefcase-medical"
        title="Health Checks"
        subtitle="View and edit health check configurations."
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      <div className="d-flex justify-content-end mb-3">
        <IconButton
          variant="success"
          size="sm"
          icon="plus"
          transform="shrink-3"
          onClick={handleOpenAddDrawer}
          className="text-xs fw-bold"
          style={{ color: "#fff" }}
        >
          <span className="ms-1">Register Health Check</span>
        </IconButton>
      </div>

      <div className="mb-2">
        <div
          className="mb-3 align-items-end gx-3 p-2 rounded bg-light row mx-auto"
          style={{ maxWidth: 980 }}
        >
          <div className="col-md-6">
            <div className="text-muted small fw-semibold mb-1">
              {"\u{1F50E} Search Health Checks"}
            </div>
            <Input
              placeholder='Search by "app", "name", "url" (min. 3 chars)...'
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              allowClear
              style={{
                width: "63%",
                borderRadius: 24,
                borderColor:
                  searchTerm.length > 0 && searchTerm.length < 3
                    ? "#ffa940"
                    : "",
              }}
              suffix={
                loading && isSearchRequest ? (
                  <Spinner animation="border" size="sm" variant="success" />
                ) : (
                  searchTerm.length > 0 &&
                  searchTerm.length < 3 && (
                    <small className="text-warning" style={{ fontSize: "10px" }}>
                      Type {3 - searchTerm.length} more...
                    </small>
                  )
                )
              }
            />
          </div>
          <div className="col-md-2 mt-3 mt-md-0">
            <div className="text-muted small fw-semibold mb-1">
              {"\u{1F3F7}\uFE0F Severity Status"}
            </div>
            <Select
              style={{ width: "100%" }}
              value={selectedStatus}
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              styles={{ popup: { root: { minWidth: 220 } } }}
              onChange={(value) => {
                setSelectedStatus(value || "ALL");
                setPage(0);
              }}
              options={statusOptions}
            />
          </div>
          <div className="col-md-2 mt-3 mt-md-0">
            <div className="text-muted small fw-semibold mb-1">
              {"\u{1F527} Enabled"}
            </div>
            <Select
              style={{ width: "100%" }}
              value={selectedEnabled}
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              styles={{ popup: { root: { minWidth: 220 } } }}
              onChange={(value) => {
                setSelectedEnabled(value || "ALL");
                setPage(0);
              }}
              options={enabledOptions}
            />
          </div>
        </div>
      </div>

      {healthChecks && healthChecks.length === 0 && !loading ? (
        <div
          style={{
            minHeight: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Empty
            description={
              searchTerm.trim() && selectedStatus !== "ALL" && selectedEnabled !== "ALL"
                ? `No health checks found for "${searchTerm.trim()}" with status "${selectedStatus}" and enabled "${selectedEnabled}".`
                : searchTerm.trim() && selectedStatus !== "ALL"
                ? `No health checks found for "${searchTerm.trim()}" with status "${selectedStatus}".`
                : searchTerm.trim() && selectedEnabled !== "ALL"
                ? `No health checks found for "${searchTerm.trim()}" with enabled "${selectedEnabled}".`
                : searchTerm.trim()
                ? `No health checks found for "${searchTerm.trim()}"`
                : selectedStatus !== "ALL" && selectedEnabled !== "ALL"
                ? `No health checks found with status "${selectedStatus}" and enabled "${selectedEnabled}".`
                : selectedStatus !== "ALL"
                ? `No health checks found with status "${selectedStatus}".`
                : selectedEnabled !== "ALL"
                ? `No health checks found with enabled "${selectedEnabled}".`
                : "No health checks have been registered yet"
            }
          />
        </div>
      ) : (
        <HealthChecksTable
          healthChecks={healthChecks}
          onEdit={(check) => handleOpenEditDrawer(check)}
          onToggleActive={handleToggleActive}
          setShowDeleteModal={setShowDeleteModal}
          setHealthCheckToDelete={setCheckToDelete}
          page={page}
          pageSize={pageSize}
          totalPages={pagination?.totalPages || 0}
          totalElements={pagination?.totalElements || 0}
          setPage={setPage}
          setPageSize={setPageSize}
          loading={loading}
        />
      )}

      {/* Drawer handles both Add and Edit based on URL/state */}
      {isDrawerOpen && (
        <HealthCheckDrawer
          show={isDrawerOpen}
          healthCheck={selectedHealthCheck}
          onClose={handleCloseDrawer}
          onSave={handleSaveHealthCheck}
          onProbe={runProbe}
          isSubmitting={isSubmitting}
        />
      )}

      <ConfirmDeleteModal
        itemName="health check"
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDeleteConfirm}
        loading={isSubmitting}
      />
    </>
  );
};

export default HealthChecks;
